import { Component, ChangeDetectionStrategy, input, output, signal, computed } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Field } from '../../../types/fields';
import { FieldTypeIconComponent } from '../../../components/field-type-icon.component';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * Vertical stacked field picker.
 *
 * Selected fields are pinned at the top (always visible, max-height scrollable).
 * Available fields appear below with search + category filter.
 *
 * Each row is a single <button> — the whole row is the click target, no nested buttons.
 * Works well at any width, including narrow side-drawers.
 */
@Component({
  selector: 'app-field-picker',
  imports: [MatIconModule, FieldTypeIconComponent, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col h-full min-h-0 rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-800">

      <!-- ── Selected section ─────────────────────────── -->
      <!-- Capped at 40% of the picker so the Available list below always keeps the majority of the space, even in short viewports. -->
      <div class="flex flex-col shrink-0 min-h-0 max-h-[40%]">
        <!-- Selected header -->
        <div class="flex items-center justify-between px-3 py-2 bg-blue-50/60 dark:bg-blue-900/10 border-b border-zinc-200 dark:border-zinc-700 shrink-0">
          <span class="text-xs font-semibold tracking-wide uppercase text-zinc-500 dark:text-zinc-400">
            Selected
            @if (selectedIds().length > 0) {
              <span class="ml-1 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 text-[10px]">{{ selectedIds().length }}</span>
            }
          </span>
          @if (selectedIds().length > 0) {
            <button
              data-testid="field-picker-clear-all-btn"
              type="button"
              (click)="removeAll()"
              class="text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium cursor-pointer transition-colors"
            >
              Clear all
            </button>
          }
        </div>

        <!-- Selected rows -->
        @if (selectedFieldObjects().length === 0) {
          <div class="px-4 py-3 text-center">
            <p class="text-xs text-zinc-400 dark:text-zinc-500 italic">Nothing selected — click fields below to add them</p>
          </div>
        } @else {
          <div class="overflow-y-auto min-h-0">
            @for (field of selectedFieldObjects(); track field.id) {
              <button
                type="button"
                (click)="removeField(field.id)"
                class="w-full flex items-center gap-2.5 px-3 py-2.5 text-left cursor-pointer group/row
                       hover:bg-red-50/70 dark:hover:bg-red-900/20
                       border-b border-zinc-50 dark:border-zinc-700/50 last:border-b-0
                       focus-visible:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-red-400
                       transition-colors"
                [matTooltip]="'Click to remove'"
                [matTooltipShowDelay]="500"
              >
                <mat-icon class="!text-sm !w-4 !h-4 !leading-4 shrink-0 text-blue-500 group-hover/row:text-red-400 transition-colors">check_circle</mat-icon>
                <app-field-type-icon [type]="field.type" size="sm" class="shrink-0"/>
                <span class="flex-1 text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{{ field.name }}</span>
                @if (field.category) {
                  <span class="text-[10px] text-zinc-400 dark:text-zinc-500 bg-zinc-100 dark:bg-zinc-700 rounded px-1.5 py-0.5 shrink-0">{{ field.category }}</span>
                }
                @if (field.required) {
                  <mat-icon class="!text-sm !w-3.5 !h-3.5 !leading-3.5 text-red-500 shrink-0" matTooltip="Required">emergency</mat-icon>
                }
                <mat-icon class="!text-sm !w-4 !h-4 !leading-4 text-blue-200 group-hover/row:text-red-400 transition-colors shrink-0">remove_circle</mat-icon>
              </button>
            }
          </div>
        }
      </div>

      <!-- ── Search + filter toolbar ──────────────────── -->
      <div class="flex items-center gap-2 px-2 py-2 border-t border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/80 shrink-0">
        <div class="relative flex-1">
          <mat-icon class="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500 !text-base pointer-events-none">search</mat-icon>
          <input
            data-testid="field-picker-search-input"
            type="text"
            placeholder="Search available fields..."
            [value]="searchQuery()"
            (input)="onSearchChange($event)"
            class="w-full pl-8 pr-2 h-8 rounded border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors placeholder:text-zinc-400 dark:placeholder:text-zinc-500 dark:text-zinc-100"
          >
        </div>
        @if (categories().length > 0) {
          <div class="relative shrink-0">
            <select
              data-testid="field-picker-category-select"
              [value]="selectedCategory()"
              (change)="onCategoryChange($event)"
              class="h-8 appearance-none rounded border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-2.5 pr-6 text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors dark:text-zinc-100 cursor-pointer"
            >
              <option value="all">All</option>
              @for (cat of categories(); track cat) {
                <option [value]="cat">{{ cat }}</option>
              }
            </select>
            <mat-icon class="absolute right-1 top-1/2 -translate-y-1/2 !text-sm text-zinc-400 dark:text-zinc-500 pointer-events-none">expand_more</mat-icon>
          </div>
        }
      </div>

      <!-- ── Available section ─────────────────────────── -->
      <div class="flex flex-col flex-1 min-h-0">
        <!-- Available header -->
        <div class="flex items-center justify-between px-3 py-1.5 bg-zinc-50/80 dark:bg-zinc-800/50 border-b border-zinc-100 dark:border-zinc-700/50 shrink-0">
          <span class="text-xs font-semibold tracking-wide uppercase text-zinc-400 dark:text-zinc-500">
            Available
            <span class="ml-1 font-normal normal-case">({{ availableFields().length }})</span>
          </span>
          @if (isFiltered() && availableFields().length > 0) {
            <button
              data-testid="field-picker-add-all-btn"
              type="button"
              (click)="addAllVisible()"
              class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium cursor-pointer transition-colors"
            >
              Add all ({{ availableFields().length }})
            </button>
          }
        </div>

        <!-- Available rows -->
        @if (availableFields().length === 0) {
          <div class="flex-1 flex flex-col items-center justify-center gap-1.5 px-4 py-6 text-center">
            <mat-icon class="text-zinc-300 dark:text-zinc-600 !text-3xl">check_circle</mat-icon>
            <p class="text-sm text-zinc-400 dark:text-zinc-500">
              @if (isFiltered()) {
                No fields match this filter
              } @else {
                All fields have been selected
              }
            </p>
          </div>
        } @else {
          <div class="flex-1 overflow-y-auto min-h-0">
            @for (field of availableFields(); track field.id) {
              <button
                type="button"
                (click)="addField(field.id)"
                class="w-full flex items-center gap-2.5 px-3 py-2.5 text-left cursor-pointer group/row
                       hover:bg-blue-50/70 dark:hover:bg-blue-900/20
                       border-b border-zinc-50 dark:border-zinc-700/50 last:border-b-0
                       focus-visible:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-blue-400
                       transition-colors"
                [matTooltip]="field.description || ''"
                [matTooltipShowDelay]="700"
              >
                <mat-icon class="!text-sm !w-4 !h-4 !leading-4 text-zinc-300 dark:text-zinc-600 group-hover/row:text-blue-500 transition-colors shrink-0">add_circle</mat-icon>
                <app-field-type-icon [type]="field.type" size="sm" class="shrink-0 opacity-70 group-hover/row:opacity-100 transition-opacity"/>
                <span class="flex-1 text-sm text-zinc-700 dark:text-zinc-300 truncate">{{ field.name }}</span>
                @if (field.category) {
                  <span class="text-[10px] text-zinc-400 dark:text-zinc-500 bg-zinc-100 dark:bg-zinc-700 rounded px-1.5 py-0.5 shrink-0">{{ field.category }}</span>
                }
                @if (field.required) {
                  <mat-icon class="!text-sm !w-3.5 !h-3.5 !leading-3.5 text-red-400 shrink-0" matTooltip="Required">emergency</mat-icon>
                }
              </button>
            }
          </div>
        }
      </div>

    </div>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }
  `]
})
export class FieldPickerComponent {
  allFields = input.required<Field[]>();
  selectedIds = input.required<string[]>();
  selectedIdsChange = output<string[]>();

  searchQuery = signal('');
  selectedCategory = signal<string>('all');

  categories = computed(() =>
    [...new Set(this.allFields().map(f => f.category).filter(Boolean))].sort()
  );

  isFiltered = computed(() => !!this.searchQuery() || this.selectedCategory() !== 'all');

  /** Available = not selected AND matches current search/category filter. */
  availableFields = computed(() => {
    const selected = new Set(this.selectedIds());
    const query = this.searchQuery().toLowerCase();
    const category = this.selectedCategory();
    return this.allFields()
      .filter(f => !selected.has(f.id))
      .filter(f => category === 'all' || f.category === category)
      .filter(f => !query || f.name.toLowerCase().includes(query) || f.description?.toLowerCase().includes(query));
  });

  /** Selected — always all selected fields, never filtered. */
  selectedFieldObjects = computed(() =>
    this.selectedIds()
      .map(id => this.allFields().find(f => f.id === id))
      .filter((f): f is Field => !!f)
  );

  onSearchChange(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }

  onCategoryChange(event: Event): void {
    this.selectedCategory.set((event.target as HTMLSelectElement).value);
  }

  addField(id: string): void {
    if (!this.selectedIds().includes(id)) {
      this.selectedIdsChange.emit([...this.selectedIds(), id]);
    }
  }

  removeField(id: string): void {
    this.selectedIdsChange.emit(this.selectedIds().filter(fid => fid !== id));
  }

  addAllVisible(): void {
    const existing = new Set(this.selectedIds());
    const toAdd = this.availableFields().map(f => f.id).filter(id => !existing.has(id));
    this.selectedIdsChange.emit([...this.selectedIds(), ...toAdd]);
  }

  removeAll(): void {
    this.selectedIdsChange.emit([]);
  }
}
